import { isStationModeEnabled } from './helpers';

export interface RegenerateLyricsParams {
  modificationRequest: string;
}

export interface RegenerateCardImageParams {
  modificationRequest: string;
  songNumber?: 1 | 2;
}

export interface RegenerateImageParams {
  modificationRequest: string;
}

export interface RegenerateResult {
  success: boolean;
  error?: string;
}

type HandleRegenerateCreationFunction = (
  args: {
    type:
      | 'lyrics'
      | 'title'
      | 'cover'
      | 'music'
      | 'image'
      | 'presentation'
      | 'businessPlan'
      | 'webpage';
    modification_request: string;
    taskId?: string;
  },
  sendFn: (msg: string) => void,
) => Promise<void>;

type SetSelectedSongIdFunction = (songId: 1 | 2 | null) => void;
type GetSelectedSongIdFunction = () => 1 | 2 | null;

let handleRegenerateCreationFunction: HandleRegenerateCreationFunction | null =
  null;
let setSelectedSongIdFunction: SetSelectedSongIdFunction | null = null;
let getSelectedSongIdFunction: GetSelectedSongIdFunction | null = null;
let presentationTaskId: string | null = null;
let businessPlanTaskId: string | null = null;
let webpageTaskId: string | null = null;

export const setPresentationTaskId = (taskId: string | null): void => {
  presentationTaskId = taskId;
};

export const setBusinessPlanTaskId = (taskId: string | null): void => {
  businessPlanTaskId = taskId;
};

export const setWebpageTaskId = (taskId: string | null): void => {
  webpageTaskId = taskId;
};

export const setRegenerationFunctions = (
  handleRegenerateCreation: HandleRegenerateCreationFunction,
  setSelectedSongId: SetSelectedSongIdFunction,
  getSelectedSongId: GetSelectedSongIdFunction,
) => {
  handleRegenerateCreationFunction = handleRegenerateCreation;
  setSelectedSongIdFunction = setSelectedSongId;
  getSelectedSongIdFunction = getSelectedSongId;
};

export const regenerateLyrics = async (
  params: RegenerateLyricsParams,
): Promise<RegenerateResult> => {
  if (!isStationModeEnabled()) {
    return {
      success: false,
      error: 'Station mode is not enabled',
    };
  }

  if (!handleRegenerateCreationFunction) {
    return {
      success: false,
      error: 'Regeneration functions not initialized',
    };
  }

  try {
    const { modificationRequest } = params;

    if (!modificationRequest || !modificationRequest.trim()) {
      return {
        success: false,
        error: 'Modification request is required',
      };
    }

    await handleRegenerateCreationFunction(
      {
        type: 'lyrics',
        modification_request: modificationRequest.trim(),
      },
      () => {},
    );

    return {
      success: true,
    };
  } catch (error) {
    if (import.meta.env.DEV) {
      console.error('Failed to regenerate lyrics:', error);
    }
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Unknown error occurred',
    };
  }
};

export const regenerateCardImage = async (
  params: RegenerateCardImageParams,
): Promise<RegenerateResult> => {
  if (!isStationModeEnabled()) {
    return {
      success: false,
      error: 'Station mode is not enabled',
    };
  }

  if (!handleRegenerateCreationFunction || !setSelectedSongIdFunction) {
    return {
      success: false,
      error: 'Regeneration functions not initialized',
    };
  }

  try {
    const { modificationRequest, songNumber } = params;

    if (!modificationRequest || !modificationRequest.trim()) {
      return {
        success: false,
        error: 'Modification request is required',
      };
    }

    const targetSongNumber = songNumber ?? getSelectedSongIdFunction?.() ?? 1;

    setSelectedSongIdFunction(targetSongNumber);

    await handleRegenerateCreationFunction(
      {
        type: 'image',
        modification_request: modificationRequest.trim(),
      },
      () => {},
    );

    return {
      success: true,
    };
  } catch (error) {
    if (import.meta.env.DEV) {
      console.error('Failed to regenerate card image:', error);
    }
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Unknown error occurred',
    };
  }
};

export const regenerateImage = async (
  params: RegenerateImageParams,
): Promise<RegenerateResult> => {
  if (!isStationModeEnabled()) {
    return {
      success: false,
      error: 'Station mode is not enabled',
    };
  }

  if (!handleRegenerateCreationFunction) {
    return {
      success: false,
      error: 'Regeneration functions not initialized',
    };
  }

  try {
    const { modificationRequest } = params;

    if (!modificationRequest || !modificationRequest.trim()) {
      return {
        success: false,
        error: 'Modification request is required',
      };
    }

    await handleRegenerateCreationFunction(
      {
        type: 'image',
        modification_request: modificationRequest.trim(),
      },
      () => {},
    );

    return {
      success: true,
    };
  } catch (error) {
    if (import.meta.env.DEV) {
      console.error('Failed to regenerate image:', error);
    }
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Unknown error occurred',
    };
  }
};

export interface RegeneratePresentationParams {
  taskId?: string;
  modificationRequest: string;
}

export const regeneratePresentation = async (
  params: RegeneratePresentationParams,
): Promise<RegenerateResult> => {
  if (!isStationModeEnabled()) {
    return {
      success: false,
      error: 'Station mode is not enabled',
    };
  }

  if (!handleRegenerateCreationFunction) {
    return {
      success: false,
      error: 'Regeneration functions not initialized',
    };
  }

  try {
    const { taskId, modificationRequest } = params;
    const effectiveTaskId = taskId || presentationTaskId;

    if (!modificationRequest || !modificationRequest.trim()) {
      return {
        success: false,
        error: 'Modification request is required',
      };
    }

    await handleRegenerateCreationFunction(
      {
        type: 'presentation',
        modification_request: modificationRequest.trim(),
        taskId: effectiveTaskId || undefined,
      },
      () => {},
    );

    return {
      success: true,
    };
  } catch (error) {
    if (import.meta.env.DEV) {
      console.error('Failed to regenerate presentation:', error);
    }
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Unknown error occurred',
    };
  }
};
export interface RegenerateBusinessPlanParams {
  taskId?: string;
  modificationRequest: string;
}

export const regenerateBusinessPlan = async (
  params: RegenerateBusinessPlanParams,
): Promise<RegenerateResult> => {
  if (!isStationModeEnabled()) {
    return {
      success: false,
      error: 'Station mode is not enabled',
    };
  }

  if (!handleRegenerateCreationFunction) {
    return {
      success: false,
      error: 'Regeneration functions not initialized',
    };
  }

  try {
    const { taskId, modificationRequest } = params;
    const effectiveTaskId = taskId || businessPlanTaskId;

    if (!modificationRequest || !modificationRequest.trim()) {
      return {
        success: false,
        error: 'Modification request is required',
      };
    }

    await handleRegenerateCreationFunction(
      {
        type: 'businessPlan',
        modification_request: modificationRequest.trim(),
        taskId: effectiveTaskId || undefined,
      },
      () => {},
    );

    return {
      success: true,
    };
  } catch (error) {
    if (import.meta.env.DEV) {
      console.error('Failed to regenerate business plan:', error);
    }
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Unknown error occurred',
    };
  }
};

export interface RegenerateWebpageParams {
  taskId?: string;
  modificationRequest: string;
}

export const regenerateWebpage = async (
  params: RegenerateWebpageParams,
): Promise<RegenerateResult> => {
  if (!isStationModeEnabled()) {
    return {
      success: false,
      error: 'Station mode is not enabled',
    };
  }

  if (!handleRegenerateCreationFunction) {
    console.log('Regeneration functions not initialized')
    return {
      success: false,
      error: 'Regeneration functions not initialized',
    };
  }

  try {
    const { taskId, modificationRequest } = params;
    const effectiveTaskId = taskId || webpageTaskId;

    if (!modificationRequest || !modificationRequest.trim()) {
      return {
        success: false,
        error: 'Modification request is required',
      };
    }

    await handleRegenerateCreationFunction(
      {
        type: 'webpage',
        modification_request: modificationRequest.trim(),
        taskId: effectiveTaskId || undefined,
      },
      () => {},
    );

    return {
      success: true,
    };
  } catch (error) {
    if (import.meta.env.DEV) {
      console.error('Failed to regenerate webpage:', error);
    }
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Unknown error occurred',
    };
  }
};
